import { FC, useState } from "react";
import { ScrollView, StyleSheet, TextInput, View } from "react-native";
import { useSelector } from "react-redux";

import { TagsInput } from "src/components";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdStack, PdText } from "src/components/layout";
import { AlertModal, tAlertModalProps } from "src/components/Modals/AlertModal";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kEmailPattern } from "src/components/UserApplicationInfo";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { repInviteBrands } from "src/redux/connectionManager/actions";
import { tModalComponentProps } from "src/redux/modals/types";
import { getApiError } from "src/util/apiNew";

type tModalParams = {
  companyId: string;
};

const DEFAULT_NOTE_MESSAGE = `Hi there,

I've started using The Main Tab, a luxury wholesale marketplace connecting the best brands, retailers, and sales reps! I'd like to invite you to join as a brand so we can work together on the platform.

As a Brand on the platform, you have the opportunity to:

• Build brand exposure and expand distribution to fine boutique stores
• Develop and strengthen relationships with your retailers
• Manage and work with your Sales Reps
• Offer your retailers NET 60, at no risk to you
• Create and share orders with your retailers, commission free
• Invite your existing retailers to join and all their orders will be commission free
• Centralize your wholesale business

Looking forward to seeing you on here soon!

Kind regards,

(name)`;

const InviteRetailersModal: FC<tModalComponentProps<tModalParams>> = ({ onClose, onResolve, companyId }) => {
  const rIsMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rCompany = useSelector(({ CompanyReducer }) =>
    CompanyReducer.type === "rep" ? CompanyReducer : { type: "rep", id: "", name: "", vendorId: undefined },
  );
  const alertModal = useModal<tAlertModalProps>(AlertModal);
  const [sEmails, setEmails] = useState<Array<string>>([]);
  const [sNote, setNote] = useState<string>(DEFAULT_NOTE_MESSAGE.replace("(name)", rCompany.name));
  const [sIsValid, setIsValid] = useState<boolean>(false);
  const [isSending, setSending] = useState(false);

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSend = async () => {
    setSending(true);

    try {
      const response = await dispatch(repInviteBrands(companyId, { emails: sEmails, note: sNote }));

      if (response.existingEmails.length > 0) {
        setSending(false);
        alertModal.openAsync({
          title: "BRAND EXISTS!",
          description:
            "One or more of the brands you invited are active on our marketplace. We've notified the brand(s) that you are now active on the marketplace!",
        });
      } else {
        onResolve();
      }
    } catch (e) {
      setSending(false);
      const error = getApiError(e);
      dispatch({ type: "RIBBON", message: new Error(error.message) });
    }
  };

  return (
    <PdModal closeOnOverlayClick={!isSending} onClose={handleOnClose}>
      <PdModalCloseButton isDisabled={isSending} onPress={handleOnClose} />
      <ScrollView style={styles.modal}>
        <PdModalTitle style={styles.mb4}>{isSending ? "Inviting your Brands..." : "Invite your Brands"}</PdModalTitle>
        {isSending ? (
          <PdText>
            Please do not close this pop up; the pop up will automatically close when the e-mails have been successfully
            sent.
          </PdText>
        ) : (
          <>
            <PdText>
              Enter email addresses for every brand you would like to invite. Separate each email address with a comma.
              Each brand will receive an individual email invitation from you to join The Main Tab.
            </PdText>
            <PdText style={styles.mt2}>Example: (name@email.com, store@email.com)</PdText>
            <View style={styles.subtitle}>
              <PdText style={styles.bold}>NOTE</PdText>
              <PdText>: Can only send to 100 e-mail addresses at a time.</PdText>
            </View>
            <View style={styles.emailFormControl}>
              <TagsInput
                onChangeTags={setEmails}
                inputStyleNoTags={styles.inputNoTags}
                inputStyleWithTags={styles.inputTags}
                placeholder="Email"
                validationPattern={kEmailPattern}
                onValidityStateChange={setIsValid}
                limitTo={300}
              />
            </View>
            <View style={styles.noteFormControl}>
              <TextInput
                style={styles.input}
                multiline={true}
                numberOfLines={8}
                value={sNote}
                onChangeText={setNote}
                placeholder="Note"
              />
            </View>
          </>
        )}
        <PdStack
          style={styles.buttonGroup}
          direction={rIsMobile ? "vertical" : "horizontal"}
          layout={[1, 1]}
          gap={3}
          isReversed={rIsMobile}
        >
          <PdButton colorScheme="purple" isDisabled={isSending} onPress={handleOnClose} variant="outline">
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton isDisabled={!sIsValid || isSending} onPress={handleOnSend}>
            <PdButtonLabel>Send</PdButtonLabel>
          </PdButton>
        </PdStack>
      </ScrollView>
    </PdModal>
  );
};

const styles = StyleSheet.create({
  bold: {
    fontWeight: "500",
  },
  buttonGroup: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  emailFormControl: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 3 * kStyleConst.kSpacing,
  },
  input: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    color: kColors.gray500,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    padding: kStyleConst.kSpacing,
  },
  inputNoTags: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    padding: kStyleConst.kSpacing,
  },
  inputTags: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    paddingHorizontal: kStyleConst.kSpacing,
    paddingVertical: 2,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  modal: {
    maxWidth: 600,
    padding: 8 * kStyleConst.kSpacing,
    width: "100%",
  },
  mt2: {
    marginTop: 2 * kStyleConst.kSpacing,
  },
  noteFormControl: {
    alignItems: "flex-start",
    flexDirection: "row",
    marginTop: 3 * kStyleConst.kSpacing,
  },
  subtitle: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 2 * kStyleConst.kSpacing,
  },
});

const useInviteRetailersModal = (): tUseModal<tModalParams> => {
  return useModal<tModalParams>(InviteRetailersModal);
};

export default useInviteRetailersModal;
